import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { Avatar } from '../common/Avatar';
import { TeamBadge } from '../common/TeamBadge';
import { DrawerOverview } from './drawer/DrawerOverview';
import { DrawerRecentCalls } from './drawer/DrawerRecentCalls';
import { DrawerTrends } from './drawer/DrawerTrends';
import { sourceLabel } from '../../utils/sources';
import type { AgentRow, VoiceSource } from '../../types/voip';
import type { FloorRow } from '../../utils/floor';
type DrawerTab = 'overview' | 'calls' | 'trends';
const tabs: {
  id: DrawerTab;
  label: string;
}[] = [{
  id: 'overview',
  label: 'Overview'
}, {
  id: 'calls',
  label: 'Recent calls'
}, {
  id: 'trends',
  label: 'Trends'
}];
interface UserDetailDrawerProps {
  row: AgentRow;
  floorRow?: FloorRow;
  floorDateLabel: string;
  scopeLabel: string;
  source: VoiceSource;
  onClose: () => void;
}
const ease = [0.23, 1, 0.32, 1] as const;
export function UserDetailDrawer({
  row,
  floorRow,
  floorDateLabel,
  scopeLabel,
  source,
  onClose
}: UserDetailDrawerProps) {
  const {
    agent
  } = row;
  const [tab, setTab] = useState<DrawerTab>('overview');
  const closeRef = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  return <div className="fixed inset-0 z-40" data-id="element-657" data-mp="6iezyzwc1er3">
      <motion.div className="absolute inset-0 bg-accent/25 backdrop-blur-[2px]" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease
    }} onClick={onClose} aria-hidden data-id="element-658" data-mp="6lkr0koiuqpg" />
      <motion.aside role="dialog" aria-modal="true" aria-labelledby="user-drawer-title" className="absolute right-0 top-0 flex h-full w-full max-w-[480px] flex-col overflow-y-auto border-l border-line bg-surface shadow-2xl" initial={{
      x: 40,
      opacity: 0
    }} animate={{
      x: 0,
      opacity: 1
    }} exit={{
      x: 40,
      opacity: 0
    }} transition={{
      duration: 0.26,
      ease
    }} data-id="element-659" data-mp="2dcaf9ki5jnjc">
        <header className="sticky top-0 z-10 border-b border-line bg-surface" data-id="element-660" data-mp="2f7o0cpnzfaem">
          <div className="flex items-start justify-between gap-4 px-6 pt-6" data-id="element-661" data-mp="2f9qunlw63pp5">
            <div className="flex items-center gap-4" data-id="element-662" data-mp="2t5vyh0wr8hrd">
              <Avatar name={agent.name} size="lg" data-id="element-663" data-mp="2p6dimjjsiwve" />
              <div data-id="element-664" data-mp="2p5lm96idcv42">
                <h2 id="user-drawer-title" className="font-display text-xl font-bold tracking-tight text-ink" data-id="element-665" data-mp="2p40ybzqk8af6">
                  {agent.name}
                </h2>
                <div className="mt-1 flex flex-wrap items-center gap-2" data-id="element-666" data-mp="2xyp7i0z9ac99">
                  <span className="text-sm text-muted" data-id="element-667" data-mp="3cumuotr7c69c">Ext. {agent.extension}</span>
                  <TeamBadge team={agent.team} data-id="element-668" data-mp="37wv6oatoh4af" />
                  <span className="text-xs text-subtle" data-id="element-669" data-mp="37y6aubr1ce22">· {sourceLabel(source)}</span>
                </div>
              </div>
            </div>
            <button ref={closeRef} type="button" onClick={onClose} aria-label="Close details" className="rounded-lg p-1.5 text-muted transition-colors duration-150 ease-out hover:bg-canvas hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-accent" data-id="element-670" data-mp="3vjgm9pw97809">
              <XIcon className="h-4 w-4" data-id="element-671" data-mp="37sus92j33o99" />
            </button>
          </div>

          <div role="tablist" aria-label="User details" className="mt-4 flex gap-6 px-6" data-id="element-672" data-mp="3clntvhj9agh4">
            {tabs.map(t => {
            const active = t.id === tab;
            return <button key={t.id} id={`drawer-tab-${t.id}`} type="button" role="tab" aria-selected={active} aria-controls="drawer-tabpanel" onClick={() => setTab(t.id)} className={`relative whitespace-nowrap pb-3 text-sm font-semibold transition-colors duration-150 ease-out focus:outline-none focus-visible:text-ink ${active ? 'text-ink' : 'text-muted hover:text-ink'}`} data-id="element-673" data-mp="33tlwjs4hhgb2">
                  {t.label}
                  {active && <motion.span layoutId="drawer-tab-underline" className="absolute inset-x-0 -bottom-px h-0.5 rounded-full bg-accent" transition={{
                duration: 0.2,
                ease
              }} data-id="element-674" data-mp="3ie9z6bo3ddfv" />}
                </button>;
          })}
          </div>
        </header>

        <AnimatePresence mode="wait" initial={false} data-id="element-675" data-mp="2zitnye912s8q">
          <motion.div key={tab} id="drawer-tabpanel" role="tabpanel" aria-labelledby={`drawer-tab-${tab}`} initial={{
          opacity: 0,
          y: 4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.16,
          ease
        }} data-id="element-676" data-mp="12p890do59we8">
            {tab === 'overview' && <DrawerOverview row={row} source={source} scopeLabel={scopeLabel} floorRow={floorRow} floorDateLabel={floorDateLabel} data-id="element-677" data-mp="1a6oaype7r6u8" />}
            {tab === 'calls' && <DrawerRecentCalls agent={agent} source={source} data-id="element-678" data-mp="1jx99kctw0bd5" />}
            {tab === 'trends' && <DrawerTrends agent={agent} source={source} data-id="element-679" data-mp="mblk6bvumtb4" />}
          </motion.div>
        </AnimatePresence>
      </motion.aside>
    </div>;
}